<template>
  <!-- Embedded mode: just the list without the widget wrapper -->
  <div v-if="embedded" class="messages-list embedded">
    <div
      v-for="conversation in unreadConversations"
      :key="conversation.roomId"
      class="list-item"
      @click="goToConversation(conversation)"
    >
      <div class="item-avatar" :style="getAvatarStyle(conversation)">
        {{ getInitials(conversation) }}
      </div>
      <div class="item-content">
        <div class="item-header">
          <span class="item-title">{{ getPatientName(conversation) }}</span>
          <span class="item-time">{{ getTimeAgo(conversation) }}</span>
        </div>
        <p class="item-preview">{{ getMessagePreview(conversation) }}</p>
      </div>
    </div>
  </div>

  <!-- Standard mode: full widget -->
  <DashboardWidget
    v-else
    title="Unread Text Messages"
    icon="fa fa-comments"
    :view-all-link="{ name: 'conversations' }"
    :loading="loading"
    :is-empty="unreadConversations.length === 0"
    empty-icon="fa fa-comments-o"
    empty-title="No unread messages"
    empty-text="All caught up!"
  >
    <div class="messages-list">
      <div
        v-for="conversation in unreadConversations"
        :key="conversation.roomId"
        class="list-item"
        @click="goToConversation(conversation)"
      >
        <div class="item-avatar" :style="getAvatarStyle(conversation)">
          {{ getInitials(conversation) }}
        </div>
        <div class="item-content">
          <div class="item-header">
            <span class="item-title">{{ getPatientName(conversation) }}</span>
            <span class="item-time">{{ getTimeAgo(conversation) }}</span>
          </div>
          <p class="item-preview">{{ getMessagePreview(conversation) }}</p>
        </div>
      </div>
    </div>
  </DashboardWidget>
</template>

<script>
import { mapState } from 'vuex';
import DashboardWidget from './DashboardWidget.vue';
import moment from 'moment-timezone';

export default {
  name: 'UnreadMessagesWidget',

  components: {
    DashboardWidget
  },

  props: {
    maxItems: {
      type: Number,
      default: 5
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    ...mapState({
      rooms: state => state.conversationsV2.rooms,
      loadingRooms: state => state.conversationsV2.loadingRooms,
      roomsLoaded: state => state.conversationsV2.roomsLoaded
    }),

    loading() {
      return this.loadingRooms && !this.roomsLoaded;
    },

    unreadConversations() {
      if (!this.rooms || !Array.isArray(this.rooms)) {
        return [];
      }

      return this.rooms
        .filter(room => {
          const unreadCount = parseInt(room.unreadCount) || 0;
          return unreadCount > 0;
        })
        .slice(0, this.maxItems);
    }
  },

  methods: {
    getPatientName(conversation) {
      return conversation.roomName ||
             conversation.patientName ||
             conversation.friendlyName ||
             'Unknown Patient';
    },

    getInitials(conversation) {
      const name = this.getPatientName(conversation);
      if (!name || name === 'Unknown Patient') return '?';

      const parts = name.split(' ').filter(p => p.length > 0);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name[0].toUpperCase();
    },

    getAvatarStyle(conversation) {
      // Generate consistent color based on room name
      const name = this.getPatientName(conversation);
      const colors = [
        '#3b82f6', // blue
        '#8b5cf6', // violet
        '#ec4899', // pink
        '#f59e0b', // amber
        '#10b981', // emerald
        '#6366f1', // indigo
      ];

      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }

      const colorIndex = Math.abs(hash) % colors.length;
      return { backgroundColor: colors[colorIndex] };
    },

    getMessagePreview(conversation) {
      const preview = conversation.lastMessagePreview ||
                     conversation.lastMessage?.content ||
                     conversation.lastMessage?.text ||
                     conversation.lastMessage?.message || '';

      // Truncate if too long
      if (preview.length > 60) {
        return preview.substring(0, 60) + '...';
      }
      return preview || 'No message preview';
    },

    getTimeAgo(conversation) {
      const timestamp = conversation.lastMessage?.timestampNum ||
                       conversation.lastMessage?.date ||
                       conversation.dateUpdated;

      if (!timestamp) return '';

      try {
        const date = typeof timestamp === 'number'
          ? moment(timestamp)
          : moment(new Date(timestamp));

        return date.fromNow();
      } catch (e) {
        return '';
      }
    },

    goToConversation(conversation) {
      this.$router.push({
        name: 'conversations',
        query: { roomId: conversation.roomId }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.messages-list {
  max-height: 300px;
  overflow-y: auto;
}
</style>
